/**
 * Источник данных текущего экрана.
 *
 * Провайдер держит обе реализации, а выдаёт ту, которую объявил маршрут. Это
 * и есть смысл слоя: экран спрашивает «дай мой набор», а не «сходи в файл» или
 * «сходи в API», и переключение источника не доходит до экрана.
 */

import {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { useAnalysisContext } from '../../analysis/context';
import { useTrack } from '../data/TrackContext';
import { corpusGeneration, subscribeCorpus } from './api/corpus';
import { principalEpoch, subscribePrincipal } from '../../api/session';
import { createApiSource } from './api';
import { createFixturesSource } from './fixtures';
import { ROUTE_SOURCES } from './routes';
import type { DataSource, RouteSource } from './types';

interface DataSourceValue {
  /** Источник, объявленный текущим маршрутом. */
  current: DataSource;
  /** Объявление маршрута — его же читает пометка о фикстурах. */
  route: RouteSource | null;
  fixtures: DataSource;
  api: DataSource;
}

const Ctx = createContext<DataSourceValue | null>(null);

/**
 * Шаблоны путей для сопоставления. Берутся из той же таблицы, что и источники:
 * второй список адресов рядом разошёлся бы с первым молча.
 */
const PATTERNS = ROUTE_SOURCES.map((row) => ({ path: row.path }));

/** Какое объявление источника отвечает этому адресу. */
export function matchRouteSource(pathname: string): RouteSource | null {
  const matched = matchRoutes(PATTERNS, pathname);
  const path = matched?.[matched.length - 1]?.route.path;
  return ROUTE_SOURCES.find((row) => row.path === path) ?? null;
}

/**
 * `pin` — адрес, по которому выбирается источник, вместо адреса окна. Нужен
 * вложенному провайдеру: виджет ИИ стоит в панели поверх ЛЮБОГО экрана, а
 * читает канон; без закрепления он получал бы источник экрана-хозяина
 * (фикстуры на `/equipment`, `/planning`, привязанные к экрану адаптеры
 * кабинета на `/my/*`) и рисовал бы не те данные или не рисовал вовсе.
 */
export function DataSourceProvider({ children, pin }: { children: ReactNode; pin?: string }) {
  const { track } = useTrack();
  const context = useAnalysisContext();
  const { pathname } = useLocation();

  // Реализации живут дольше маршрута.
  //
  // Раньше обе пересоздавались на КАЖДОМ переходе (в зависимостях стоял
  // `pathname`), а `useTrackData` держит источник в зависимостях эффекта —
  // значит каждый переход перечитывал все наборы заново, включая наборы
  // оболочки, которые не менялись вовсе. Источник зависит от трека и от
  // среза, а не от адреса; от адреса зависит только ВЫБОР между ними.
  const fixtures = useMemo(() => createFixturesSource(track), [track]);
  const route = useMemo(() => matchRouteSource(pin ?? pathname), [pin, pathname]);
  // Реализация `api` знает СВОЙ маршрут: адаптеры подключены по доменам, и
  // набор одного экрана не должен отвечать на чужом.
  // Запись двигает поколение корпуса, и источник пересоздаётся: внесённый
  // отчёт обязан быть виден на следующем чтении, а не после перезагрузки.
  const generation = useSyncExternalStore(subscribeCorpus, corpusGeneration, corpusGeneration);
  // Смена аккаунта — тоже новый источник: ответы срезаны областью чтения
  // вошедшего, и память прежнего сброшена (`api/session.ts`).
  const principal = useSyncExternalStore(subscribePrincipal, principalEpoch, principalEpoch);
  const api = useMemo(
    () => createApiSource(context.params, context.viewParams, route?.path ?? null),
    [context.params, context.viewParams, route, generation, principal],
  );

  const value = useMemo<DataSourceValue>(
    () => ({
      fixtures,
      api,
      route,
      // Маршрут без объявления — не повод молча уйти в API: неизвестный экран
      // питается фикстурами, и пометка об этом видна, пока источник не назван.
      current: route?.source === 'api' ? api : fixtures,
    }),
    [fixtures, api, route],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDataSource(): DataSourceValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useDataSource вызван вне DataSourceProvider');
  return value;
}

/** Объявление источника текущего маршрута — для пометки в оболочке. */
export function useRouteSource(): RouteSource | null {
  return useDataSource().route;
}
